import React, { useEffect, useMemo, useState } from 'react';
import { settingsService } from '../services/api';
import '../styles/Settings.css';

const CATEGORY_OPTIONS = [
  { value: 'DOCUMENT_VERIFICATION', label: 'Document Verification' },
  { value: 'DRAFT_LLM', label: 'Draft LLM' },
  { value: 'RESEARCH_LLM', label: 'Research LLM' },
  { value: 'DOCUMENT_REVIEW_LLM', label: 'Document Review LLM' },
  { value: 'CONTRACT_REVIEW_LLM', label: 'Contract Review LLM' },
  { value: 'CASE_CUSTOMER_AGENT_LLM', label: 'Customer Case Agent LLM' },
  { value: 'PAYMENT_PROCESSOR', label: 'Payment Processor' },
];

const emptyProvider = {
  name: '',
  apiEndpoint: '',
  apiKey: '',
  modelName: '',
  authHeaderName: 'Authorization',
  authHeaderPrefix: 'Bearer ',
  categories: [],
  isActive: true,
};

const emptyWorkflow = {
  name: '',
  workflowKey: 'AI_FIRST_CASE_LIFECYCLE',
  version: '',
  description: '',
  playbookJson: '{\n  "stages": []\n}',
  rulesJson: '{\n  "escalationRules": []\n}',
  isActive: true,
};

const currentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end.toISOString().slice(0, 10),
    pricingModel: 'AGENTIC_CONSUMPTION',
  };
};

function ServiceProviderForm({ formData, saving, onChange, onCategoryChange, onSubmit, onCancel }) {
  return (
    <form className="settings-form" onSubmit={onSubmit}>
      <div className="form-row">
        <label>
          Company Name
          <input name="name" value={formData.name} onChange={onChange} required />
        </label>
        <label>
          API Endpoint
          <input name="apiEndpoint" value={formData.apiEndpoint} onChange={onChange} required />
        </label>
        <label>
          Model Name
          <input name="modelName" value={formData.modelName || ''} onChange={onChange} />
        </label>
      </div>

      <div className="form-row">
        <label>
          API Key
          <input name="apiKey" value={formData.apiKey || ''} onChange={onChange} type="password" />
        </label>
        <label>
          Auth Header
          <input name="authHeaderName" value={formData.authHeaderName} onChange={onChange} />
        </label>
        <label>
          Header Prefix
          <input name="authHeaderPrefix" value={formData.authHeaderPrefix} onChange={onChange} />
        </label>
      </div>

      <div className="category-picker">
        <span>Services Provided</span>
        <div className="category-options">
          {CATEGORY_OPTIONS.map((category) => (
            <label key={category.value} className="checkbox-label category-option">
              <input
                type="checkbox"
                checked={(formData.categories || []).includes(category.value)}
                onChange={(e) => onCategoryChange(category.value, e.target.checked)}
              />
              {category.label}
            </label>
          ))}
        </div>
      </div>

      <label className="checkbox-label">
        <input name="isActive" type="checkbox" checked={formData.isActive} onChange={onChange} />
        Active
      </label>

      <div className="form-actions">
        <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Provider'}</button>
        <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function ProviderSection({ title, category, items, onEdit, onDelete }) {
  return (
    <section className="settings-section">
      <div className="settings-section-header">
        <div>
          <h3>{title}</h3>
          <p>{items.length} configured</p>
        </div>
      </div>

      <div className="settings-table-wrap">
        <table className="settings-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Endpoint</th>
              {category !== 'DOCUMENT_VERIFICATION' && <th>Model</th>}
              <th>Services</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={category !== 'DOCUMENT_VERIFICATION' ? 6 : 5} className="empty-row">
                  No providers configured.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.apiEndpoint}</td>
                  {category !== 'DOCUMENT_VERIFICATION' && <td>{item.modelName || '-'}</td>}
                  <td>
                    <div className="category-badges">
                      {(item.categories || []).map((serviceCategory) => (
                        <span key={serviceCategory}>{serviceCategory.replaceAll('_', ' ')}</span>
                      ))}
                    </div>
                  </td>
                  <td>{item.isActive ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="edit-provider-btn" onClick={() => onEdit(item)}>
                        Edit
                      </button>
                      <button type="button" className="delete-provider-btn" onClick={() => onDelete(item.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LlmUsageDashboard({ usage, loading, error, onRefresh }) {
  const money = (value) => `$${Number(value || 0).toFixed(4)}`;

  return (
    <section className="settings-section usage-dashboard">
      <div className="settings-section-header">
        <div>
          <h3>LLM Usage Dashboard</h3>
          <p>Estimated usage and cost based on captured provider calls.</p>
        </div>
        <button type="button" className="add-provider-btn" onClick={onRefresh}>
          {loading ? 'Refreshing...' : 'Refresh Usage'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="usage-metrics">
        <div>
          <span>Total Requests</span>
          <strong>{usage?.totalRequests || 0}</strong>
        </div>
        <div>
          <span>Successful</span>
          <strong>{usage?.successfulRequests || 0}</strong>
        </div>
        <div>
          <span>Failed</span>
          <strong>{usage?.failedRequests || 0}</strong>
        </div>
        <div>
          <span>Estimated Tokens</span>
          <strong>{usage?.totalTokens || 0}</strong>
        </div>
        <div>
          <span>Estimated Cost</span>
          <strong>{money(usage?.estimatedCost)}</strong>
        </div>
      </div>

      <div className="settings-table-wrap usage-table">
        <table className="settings-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Requests</th>
              <th>Success</th>
              <th>Failed</th>
              <th>Tokens</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {(usage?.providerUsage || []).length === 0 ? (
              <tr><td colSpan="6" className="empty-row">No LLM usage captured yet.</td></tr>
            ) : (
              usage.providerUsage.map((provider) => (
                <tr key={`${provider.providerId || 'unknown'}-${provider.providerName}`}>
                  <td>{provider.providerName}</td>
                  <td>{provider.requestCount}</td>
                  <td>{provider.successCount}</td>
                  <td>{provider.failureCount}</td>
                  <td>{provider.totalTokens}</td>
                  <td>{money(provider.estimatedCost)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="settings-table-wrap usage-table">
        <table className="settings-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Provider</th>
              <th>Service</th>
              <th>Resource</th>
              <th>Status</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {(usage?.recentLogs || []).length === 0 ? (
              <tr><td colSpan="6" className="empty-row">No recent LLM calls.</td></tr>
            ) : (
              usage.recentLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.createdAt ? new Date(log.createdAt).toLocaleString() : '-'}</td>
                  <td>{log.providerName}</td>
                  <td>{(log.category || '').replaceAll('_', ' ')}</td>
                  <td>{log.resourceType || '-'}</td>
                  <td>{log.status}</td>
                  <td>{money(log.estimatedCost)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AgenticTelemetryPanel({
  formData,
  preview,
  loading,
  publishing,
  error,
  success,
  onChange,
  onPreview,
  onPublish,
}) {
  const metrics = preview?.metrics || [];
  const primaryMetrics = metrics.filter((metric) => [
    'ACTIVE_USERS',
    'CASES_CREATED',
    'AI_DRAFTS_GENERATED',
    'TOKEN_USAGE',
    'PAYMENTS_PROCESSED',
  ].includes(metric.metricKey));

  return (
    <section className="settings-section telemetry-panel">
      <div className="settings-section-header">
        <div>
          <h3>Agentic CS Telemetry</h3>
          <p>Preview and publish LibraLegal usage snapshots to Agentic CS Command Center.</p>
        </div>
      </div>

      <form className="settings-form telemetry-form" onSubmit={onPreview}>
        <div className="form-row">
          <label>
            Period Start
            <input name="periodStart" type="date" value={formData.periodStart} onChange={onChange} required />
          </label>
          <label>
            Period End
            <input name="periodEnd" type="date" value={formData.periodEnd} onChange={onChange} required />
          </label>
          <label>
            Pricing Model
            <input name="pricingModel" value={formData.pricingModel} onChange={onChange} />
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Previewing...' : 'Preview Snapshot'}
          </button>
          <button type="button" className="add-provider-btn" onClick={onPublish} disabled={publishing}>
            {publishing ? 'Publishing...' : 'Publish to Agentic CS'}
          </button>
        </div>
      </form>

      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      {preview && (
        <>
          <div className="telemetry-summary">
            <div>
              <span>Tenant</span>
              <strong>{preview.tenantId}</strong>
            </div>
            <div>
              <span>Source</span>
              <strong>{preview.sourceSystem}</strong>
            </div>
            <div>
              <span>Period</span>
              <strong>{preview.periodStart} to {preview.periodEnd}</strong>
            </div>
            <div>
              <span>Metrics</span>
              <strong>{metrics.length}</strong>
            </div>
          </div>

          <div className="usage-metrics telemetry-metrics">
            {primaryMetrics.map((metric) => (
              <div key={metric.metricKey}>
                <span>{metric.metricName}</span>
                <strong>{Number(metric.metricValue || 0).toLocaleString()} {metric.metricUnit}</strong>
              </div>
            ))}
          </div>

          <div className="settings-table-wrap usage-table">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Category</th>
                  <th>Value</th>
                  <th>Trend</th>
                  <th>Health</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric) => (
                  <tr key={metric.metricKey}>
                    <td>{metric.metricName}</td>
                    <td>{String(metric.metricCategory || '').replaceAll('_', ' ')}</td>
                    <td>{Number(metric.metricValue || 0).toLocaleString()} {metric.metricUnit}</td>
                    <td>{metric.trendDirection || '-'}</td>
                    <td>{metric.healthSignal || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function AgenticMonitoringPanel({
  lookbackMinutes,
  preview,
  loading,
  publishing,
  error,
  success,
  onLookbackChange,
  onPreview,
  onPublish,
}) {
  const metrics = preview?.metrics || [];
  const primaryMetrics = metrics.filter((metric) => [
    'APP_RESPONSE_TIME_MS',
    'API_ERROR_RATE',
    'FAILED_LOGIN_COUNT',
    'INTEGRATION_FAILURES',
    'QUEUE_BACKLOG_SIZE',
  ].includes(metric.metricKey));

  return (
    <section className="settings-section telemetry-panel">
      <div className="settings-section-header">
        <div>
          <h3>Application Monitoring</h3>
          <p>Preview and publish uptime, response, failures, backlog, and adoption heartbeats to Agentic CS.</p>
        </div>
      </div>

      <form className="settings-form telemetry-form" onSubmit={onPreview}>
        <div className="form-row">
          <label>
            Lookback Minutes
            <input
              name="lookbackMinutes"
              type="number"
              min="1"
              max="1440"
              value={lookbackMinutes}
              onChange={onLookbackChange}
            />
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Previewing...' : 'Preview Heartbeat'}
          </button>
          <button type="button" className="add-provider-btn" onClick={onPublish} disabled={publishing}>
            {publishing ? 'Publishing...' : 'Publish Monitoring Heartbeat'}
          </button>
        </div>
      </form>

      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      {preview && (
        <>
          <div className="telemetry-summary">
            <div>
              <span>Status</span>
              <strong>{preview.status}</strong>
            </div>
            <div>
              <span>Environment</span>
              <strong>{preview.environment}</strong>
            </div>
            <div>
              <span>Window</span>
              <strong>{new Date(preview.periodStart).toLocaleTimeString()} - {new Date(preview.periodEnd).toLocaleTimeString()}</strong>
            </div>
            <div>
              <span>Metrics</span>
              <strong>{metrics.length}</strong>
            </div>
          </div>

          <div className="usage-metrics telemetry-metrics">
            {primaryMetrics.map((metric) => (
              <div key={metric.metricKey}>
                <span>{metric.metricName}</span>
                <strong>{Number(metric.metricValue || 0).toLocaleString()} {metric.metricUnit}</strong>
              </div>
            ))}
          </div>

          <div className="settings-table-wrap usage-table">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Category</th>
                  <th>Value</th>
                  <th>Health</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric) => (
                  <tr key={metric.metricKey}>
                    <td>{metric.metricName}</td>
                    <td>{String(metric.metricCategory || '').replaceAll('_', ' ')}</td>
                    <td>{Number(metric.metricValue || 0).toLocaleString()} {metric.metricUnit}</td>
                    <td>{metric.healthSignal || '-'}</td>
                    <td>{metric.evidenceNotes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function WorkflowPlaybooks({ workflows, formData, saving, error, showForm, onChange, onSubmit, onEdit, onCancel, onNew, onToggleActive }) {
  return (
    <section className="settings-section">
      <div className="settings-section-header">
        <div>
          <h3>AI Workflow Playbooks</h3>
          <p>Admin-managed lifecycle definitions used by the Case Agent orchestration layer.</p>
        </div>
        <button type="button" className="add-provider-btn" onClick={onNew}>
          Add Playbook
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {showForm && (
        <form className="settings-form workflow-form" onSubmit={onSubmit}>
          <div className="form-row">
            <label>
              Name
              <input name="name" value={formData.name} onChange={onChange} required />
            </label>
            <label>
              Workflow Key
              <input name="workflowKey" value={formData.workflowKey} onChange={onChange} required />
            </label>
            <label>
              Version
              <input name="version" type="number" value={formData.version || ''} onChange={onChange} />
            </label>
          </div>

          <label>
            Description
            <textarea name="description" value={formData.description || ''} onChange={onChange} rows="2" />
          </label>

          <label>
            Playbook JSON
            <textarea name="playbookJson" value={formData.playbookJson || ''} onChange={onChange} rows="14" required />
          </label>

          <label>
            Rules JSON
            <textarea name="rulesJson" value={formData.rulesJson || ''} onChange={onChange} rows="8" />
          </label>

          <label className="checkbox-label">
            <input name="isActive" type="checkbox" checked={Boolean(formData.isActive)} onChange={onChange} />
            Active
          </label>

          <div className="form-actions">
            <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Playbook'}</button>
            <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
          </div>
        </form>
      )}

      <div className="settings-table-wrap">
        <table className="settings-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Version</th>
              <th>Active</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {workflows.length === 0 ? (
              <tr><td colSpan="6" className="empty-row">No workflow playbooks configured.</td></tr>
            ) : (
              workflows.map((workflow) => (
                <tr key={workflow.id}>
                  <td>{workflow.name}</td>
                  <td>{workflow.workflowKey}</td>
                  <td>{workflow.version}</td>
                  <td>{workflow.isActive ? 'Yes' : 'No'}</td>
                  <td>{workflow.updatedAt ? new Date(workflow.updatedAt).toLocaleString() : '-'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="edit-provider-btn" onClick={() => onEdit(workflow)}>
                        Edit
                      </button>
                      <button type="button" className="delete-provider-btn" onClick={() => onToggleActive(workflow)}>
                        {workflow.isActive ? 'Pause' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Settings() {
  const [activeSettingsTab, setActiveSettingsTab] = useState('usage');
  const [providers, setProviders] = useState([]);
  const [formData, setFormData] = useState(emptyProvider);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');
  const [workflows, setWorkflows] = useState([]);
  const [workflowForm, setWorkflowForm] = useState(emptyWorkflow);
  const [showWorkflowForm, setShowWorkflowForm] = useState(false);
  const [workflowSaving, setWorkflowSaving] = useState(false);
  const [workflowError, setWorkflowError] = useState('');
  const [telemetryForm, setTelemetryForm] = useState(currentMonthRange);
  const [telemetryPreview, setTelemetryPreview] = useState(null);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [telemetryPublishing, setTelemetryPublishing] = useState(false);
  const [telemetryError, setTelemetryError] = useState('');
  const [telemetrySuccess, setTelemetrySuccess] = useState('');
  const [monitoringLookbackMinutes, setMonitoringLookbackMinutes] = useState(15);
  const [monitoringPreview, setMonitoringPreview] = useState(null);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringPublishing, setMonitoringPublishing] = useState(false);
  const [monitoringError, setMonitoringError] = useState('');
  const [monitoringSuccess, setMonitoringSuccess] = useState('');

  useEffect(() => {
    loadProviders();
    loadUsage();
    loadWorkflows();
  }, []);

  const groupedProviders = useMemo(() => ({
    DOCUMENT_VERIFICATION: providers.filter((provider) => provider.categories?.includes('DOCUMENT_VERIFICATION')),
    DRAFT_LLM: providers.filter((provider) => provider.categories?.includes('DRAFT_LLM')),
    RESEARCH_LLM: providers.filter((provider) => provider.categories?.includes('RESEARCH_LLM')),
    DOCUMENT_REVIEW_LLM: providers.filter((provider) => provider.categories?.includes('DOCUMENT_REVIEW_LLM')),
    CONTRACT_REVIEW_LLM: providers.filter((provider) => provider.categories?.includes('CONTRACT_REVIEW_LLM')),
    CASE_CUSTOMER_AGENT_LLM: providers.filter((provider) => provider.categories?.includes('CASE_CUSTOMER_AGENT_LLM')),
    PAYMENT_PROCESSOR: providers.filter((provider) => provider.categories?.includes('PAYMENT_PROCESSOR')),
  }), [providers]);

  const loadProviders = async () => {
    setError('');
    try {
      const response = await settingsService.getServiceProviders(false);
      setProviders(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to load service providers');
    }
  };

  const loadWorkflows = async () => {
    setWorkflowError('');
    try {
      const response = await settingsService.getWorkflowPlaybooks();
      setWorkflows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setWorkflowError('Failed to load workflow playbooks');
    }
  };

  const updateForm = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const updateWorkflowForm = (e) => {
    const { name, value, type, checked } = e.target;
    setWorkflowForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const updateCategory = (category, checked) => {
    setFormData((prev) => {
      const categories = new Set(prev.categories || []);
      if (checked) {
        categories.add(category);
      } else {
        categories.delete(category);
      }
      return { ...prev, categories: Array.from(categories) };
    });
  };

  const openNewProviderForm = () => {
    setFormData(emptyProvider);
    setShowForm(true);
  };

  const openNewWorkflowForm = () => {
    setWorkflowForm(emptyWorkflow);
    setShowWorkflowForm(true);
  };

  const editWorkflow = (workflow) => {
    setWorkflowForm({
      ...emptyWorkflow,
      ...workflow,
      version: workflow.version || '',
    });
    setShowWorkflowForm(true);
  };

  const editProvider = (provider) => {
    setFormData({
      ...emptyProvider,
      ...provider,
      categories: provider.categories || [],
    });
    setShowForm(true);
  };

  const saveProvider = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await settingsService.saveServiceProvider(formData);
      setFormData(emptyProvider);
      setShowForm(false);
      await loadProviders();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setError(responseMessage || 'Failed to save service provider');
    } finally {
      setSaving(false);
    }
  };

  const saveWorkflow = async (e) => {
    e.preventDefault();
    setWorkflowSaving(true);
    setWorkflowError('');

    try {
      const payload = {
        ...workflowForm,
        version: workflowForm.version ? Number(workflowForm.version) : null,
      };
      await settingsService.saveWorkflowPlaybook(payload);
      setWorkflowForm(emptyWorkflow);
      setShowWorkflowForm(false);
      await loadWorkflows();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setWorkflowError(responseMessage || 'Failed to save workflow playbook');
    } finally {
      setWorkflowSaving(false);
    }
  };

  const toggleWorkflowActive = async (workflow) => {
    setWorkflowError('');
    try {
      await settingsService.setWorkflowPlaybookActive(workflow.id, !workflow.isActive);
      await loadWorkflows();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setWorkflowError(responseMessage || 'Failed to update workflow playbook');
    }
  };

  const deleteProvider = async (id) => {
    setError('');
    try {
      await settingsService.deleteServiceProvider(id);
      await loadProviders();
    } catch (err) {
      setError('Failed to delete service provider');
    }
  };

  const loadUsage = async () => {
    setUsageLoading(true);
    setUsageError('');
    try {
      const response = await settingsService.getLlmUsage();
      setUsage(response.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setUsageError(responseMessage || `Failed to load LLM usage${err.response?.status ? ` (HTTP ${err.response.status})` : ''}`);
      setUsage(null);
    } finally {
      setUsageLoading(false);
    }
  };

  const updateTelemetryForm = (e) => {
    const { name, value } = e.target;
    setTelemetryForm((prev) => ({ ...prev, [name]: value }));
  };

  const previewTelemetry = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setTelemetryLoading(true);
    setTelemetryError('');
    setTelemetrySuccess('');
    try {
      const response = await settingsService.previewAgenticUsageSnapshot(telemetryForm);
      setTelemetryPreview(response.data);
      return response.data;
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setTelemetryError(responseMessage || 'Failed to preview Agentic CS usage snapshot');
      return null;
    } finally {
      setTelemetryLoading(false);
    }
  };

  const publishTelemetry = async () => {
    setTelemetryPublishing(true);
    setTelemetryError('');
    setTelemetrySuccess('');
    try {
      const response = await settingsService.publishAgenticUsageSnapshot(telemetryForm);
      setTelemetryPreview(response.data?.snapshot || null);
      setTelemetrySuccess(`Published ${response.data?.snapshot?.metrics?.length || 0} metrics to Agentic CS.`);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.responseBody || err.response?.data?.message || err.response?.data?.error;
      const httpStatus = err.response?.status ? `HTTP ${err.response.status}: ` : '';
      setTelemetryError(responseMessage || `${httpStatus}${err.message || 'Failed to publish Agentic CS usage snapshot'}`);
    } finally {
      setTelemetryPublishing(false);
    }
  };

  const previewMonitoring = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setMonitoringLoading(true);
    setMonitoringError('');
    setMonitoringSuccess('');
    try {
      const response = await settingsService.previewAgenticMonitoringHeartbeat(monitoringLookbackMinutes);
      setMonitoringPreview(response.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setMonitoringError(responseMessage || 'Failed to preview application monitoring heartbeat');
    } finally {
      setMonitoringLoading(false);
    }
  };

  const publishMonitoring = async () => {
    setMonitoringPublishing(true);
    setMonitoringError('');
    setMonitoringSuccess('');
    try {
      const response = await settingsService.publishAgenticMonitoringHeartbeat(monitoringLookbackMinutes);
      setMonitoringPreview(response.data?.heartbeat || null);
      setMonitoringSuccess(`Published ${response.data?.heartbeat?.metrics?.length || 0} monitoring metrics to Agentic CS.`);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.responseBody || err.response?.data?.message;
      setMonitoringError(responseMessage || 'Failed to publish application monitoring heartbeat');
    } finally {
      setMonitoringPublishing(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-page-header">
        <div>
          <h2>Settings</h2>
          <p>Manage AI usage, providers, and workflow playbooks for Libra Legal.</p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="settings-tabs">
        <button
          type="button"
          className={activeSettingsTab === 'usage' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('usage')}
        >
          AI Service Usage
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'providers' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('providers')}
        >
          AI Service Providers
        </button>
        <button
          type="button"
          className={activeSettingsTab === 'playbooks' ? 'active' : ''}
          onClick={() => setActiveSettingsTab('playbooks')}
        >
          AI Workflow Playbooks
        </button>
      </div>

      {activeSettingsTab === 'usage' && (
        <>
          <LlmUsageDashboard usage={usage} loading={usageLoading} error={usageError} onRefresh={loadUsage} />
          <AgenticTelemetryPanel
            formData={telemetryForm}
            preview={telemetryPreview}
            loading={telemetryLoading}
            publishing={telemetryPublishing}
            error={telemetryError}
            success={telemetrySuccess}
            onChange={updateTelemetryForm}
            onPreview={previewTelemetry}
            onPublish={publishTelemetry}
          />
          <AgenticMonitoringPanel
            lookbackMinutes={monitoringLookbackMinutes}
            preview={monitoringPreview}
            loading={monitoringLoading}
            publishing={monitoringPublishing}
            error={monitoringError}
            success={monitoringSuccess}
            onLookbackChange={(e) => setMonitoringLookbackMinutes(e.target.value)}
            onPreview={previewMonitoring}
            onPublish={publishMonitoring}
          />
        </>
      )}

      {activeSettingsTab === 'providers' && (
        <>
          <section className="settings-section settings-tab-intro">
            <div className="settings-section-header">
              <div>
                <h3>AI Service Providers</h3>
                <p>Configure each company once, then choose all AI services it supports.</p>
              </div>
              <button type="button" className="add-provider-btn" onClick={openNewProviderForm}>
                Add Service Provider
              </button>
            </div>

            {showForm && (
              <ServiceProviderForm
                formData={formData}
                saving={saving}
                onChange={updateForm}
                onCategoryChange={updateCategory}
                onSubmit={saveProvider}
                onCancel={() => setShowForm(false)}
              />
            )}
          </section>

          <ProviderSection
            title="Document Verification Companies"
            category="DOCUMENT_VERIFICATION"
            items={groupedProviders.DOCUMENT_VERIFICATION}
            onEdit={editProvider}
            onDelete={deleteProvider}
          />

          <ProviderSection
            title="Draft LLM Companies"
            category="DRAFT_LLM"
            items={groupedProviders.DRAFT_LLM}
            onEdit={editProvider}
            onDelete={deleteProvider}
          />

          <ProviderSection
            title="Research LLM Companies"
            category="RESEARCH_LLM"
            items={groupedProviders.RESEARCH_LLM}
            onEdit={editProvider}
            onDelete={deleteProvider}
          />

          <ProviderSection
            title="Document Review LLM Companies"
            category="DOCUMENT_REVIEW_LLM"
            items={groupedProviders.DOCUMENT_REVIEW_LLM}
            onEdit={editProvider}
            onDelete={deleteProvider}
          />

          <ProviderSection
            title="Contract Review LLM Companies"
            category="CONTRACT_REVIEW_LLM"
            items={groupedProviders.CONTRACT_REVIEW_LLM}
            onEdit={editProvider}
            onDelete={deleteProvider}
          />

          <ProviderSection
            title="Customer Case Agent LLM Companies"
            category="CASE_CUSTOMER_AGENT_LLM"
            items={groupedProviders.CASE_CUSTOMER_AGENT_LLM}
            onEdit={editProvider}
            onDelete={deleteProvider}
          />

          <ProviderSection
            title="Payment Processor Companies"
            category="PAYMENT_PROCESSOR"
            items={groupedProviders.PAYMENT_PROCESSOR}
            onEdit={editProvider}
            onDelete={deleteProvider}
          />
        </>
      )}

      {activeSettingsTab === 'playbooks' && (
        <WorkflowPlaybooks
          workflows={workflows}
          formData={workflowForm}
          saving={workflowSaving}
          error={workflowError}
          showForm={showWorkflowForm}
          onChange={updateWorkflowForm}
          onSubmit={saveWorkflow}
          onEdit={editWorkflow}
          onCancel={() => setShowWorkflowForm(false)}
          onNew={openNewWorkflowForm}
          onToggleActive={toggleWorkflowActive}
        />
      )}
    </div>
  );
}
